import {
  Component,
  Host,
  h,
  Element,
  Prop,
  Method,
  Event,
  EventEmitter,
  Watch,
  State
} from "@stencil/core";

import {
  StzhCarouselSlideStartEvent,
  StzhCarouselSlidePassedEvent,
  StzhCarouselSlideEndEvent
} from "../../index";

import { isStzhElement } from "../../utils/utils";
import { StzhCarouselLocalizedText } from "./stzh-carousel.localization"

import {
  media,
  addMediaChangeListener,
  removeMediaChangeListener
} from '../../utils/media-utils';

const reducedMotionMedia = window.matchMedia("(prefers-reduced-motion: reduce)");

let carouselCounter = 0;

/*
* Inspired by:
*
* - https://github.com/barthy-koeln/scroll-snap-slider
* - https://tannerhodges.github.io/snap-slider/
* - https://www.w3.org/WAI/ARIA/apg/patterns/carousel/examples/carousel-1-prev-next/
* - https://dev.to/jasonwebb/how-to-build-a-more-accessible-carousel-or-slider-35lp
* - http://web-accessibility.carnegiemuseums.org/code/carousels/
*/

/**
 * @slot - Slot for any element
 * @slot actions - Slot for `stzh-actions` element
 */
@Component({
  tag: "stzh-carousel",
  styleUrl: "stzh-carousel.scss",
  scoped: true
})
export class StzhCarousel {
  /** Translation strings */
  @Prop() localization: StzhCarouselLocalizedText;

  /** Carousel variant */
  @Prop({ reflect: true }) variant: "default" | "cards" = "default";

  /** Whether carousel is disabled */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** Whether to remove inside/outside peek. */
  @Prop({ reflect: true }) noPeek: boolean = false;

  /** Slides number */
  @Prop() slides: number = 1;
  /** Slides number above small breakpoint */
  @Prop() slidesSmall: number = 0;
  /** Slides number above medium breakpoint */
  @Prop() slidesMedium: number = 0;
  /** Slides number above large breakpoint */
  @Prop() slidesLarge: number = 0;
  /** Slides number above ultra breakpoint */
  @Prop() slidesUltra: number = 0;

  // /** How many slides per swipe */
  // @Prop() swipeSlides: number = 1;
  // /** How many slides per swipe above small breakpoint */
  // @Prop() swipeSlidesSmall: number = 0;
  // /** How many slides per swipe above medium breakpoint */
  // @Prop() swipeSlidesMedium: number = 0;
  // /** How many slides per swipe above large breakpoint */
  // @Prop() swipeSlidesLarge: number = 0;
  // /** How many slides per swipe above ultra breakpoint */
  // @Prop() swipeSlidesUltra: number = 0;

  /** Active slide (readonly) */
  @Prop() slide: number = 0;

  /**
   * Whether autoplay is enabled.
   * Gets paused, when user has focus inside carousel element or hovers over it.
   * Also when reduced motion media query is active or carousel is set disabled.
   * Gets disabled permanently, when user interacts with carousel manually (clicks prev/next, scrolls or drags)
   */
  @Prop({ mutable: true }) autoplay: boolean = false;

  /** Timeout delay in milliseconds used for autoplay */
  @Prop() autoplayTimeout: number = 3000;

  /** Whether scrolling & dragging should be disabled */
  @Prop() nonInteractive: boolean = false;

  /** Whether drag with mouse should be disabled (still will be able to scroll with wheeling or arrow keys) */
  @Prop() preventMouseDrag: boolean = false;

  /** Whether autoplay pause should be prevented when user hovers/focuses carousel */
  @Prop() preventAutoplayPause: boolean = false;

  /** Whether autoplay stop should be prevented when user scroll or drags carousel */
  @Prop() preventAutoplayStop: boolean = false;

  /**
   * Enable all slides to be tabbable (will disable prev/next arrows from being tabbable).
   * By default only the visible slides will be tabbable and the prev/next can be used to change slides
   * (enabled when `variant` is set to `cards`).
   */
  @Prop() tabThrough: boolean = false;

  /** Whether indicator should be shown */
  @Prop() withTrack: boolean = false;


  /** Timeout delay in milliseconds used to catch the end of scroll events */
  @State() scrollTimeout: number = 100;

  /** Minimum drag distance that needs to be reached to swipe to next/previous slide(s). */
  @State() distanceQuickSwipe: number = 40;

  /** Minimum drag distance that neeeds to be reached to prevent click on slide elements. */
  @State() distancePreventClick: number = 6;

  @Element() element: HTMLStzhCarouselElement;

  /** Slide start event */
  @Event() stzhSlideStart: EventEmitter<StzhCarouselSlideStartEvent>;

  /** Slide passed event */
  @Event() stzhSlidePassed: EventEmitter<StzhCarouselSlidePassedEvent>;

  /** Slide end event */
  @Event() stzhSlideEnd: EventEmitter<StzhCarouselSlideEndEvent>;

  /** Scroll to a slide by index */
  @Method()
  async slideTo(index, behaviour: "auto" | "smooth" = "smooth") {
    this.scrollToSlide(index, behaviour);
  }

  /** Scroll to previous snap point */
  @Method()
  async slideToPrevious(behaviour: "auto" | "smooth" = "smooth") {
    this.scrollToPreviousSnapPoint(behaviour);
  }

  /** Scroll to next snap point */
  @Method()
  async slideToNext(behaviour: "auto" | "smooth" = "smooth") {
    this.scrollToNextSnapPoint(behaviour);
  }

  @Watch("disabled")
  disabledWatcher() {
    if (this.disabled) {
      this.scrollElement && this.scrollElement.removeEventListener("scroll", this.onScroll);
      this.scrollTimeoutId && window.clearTimeout(this.scrollTimeoutId);
      this.scrollTimeoutId = null;
      this.element.removeEventListener("mouseenter", this.onMouseenter);
      this.element.removeEventListener("mouseleave", this.onMouseleave);
      this.element.removeEventListener("focusin", this.onFocusin);
      this.element.removeEventListener("focusout", this.onFocusout);
      this.onReducedMotionMediaChange();
      reducedMotionMedia && reducedMotionMedia.removeEventListener("change", this.onReducedMotionMediaChange);
    } else {
      this.scrollElement && this.scrollElement.addEventListener("scroll", this.onScroll, { passive: true });
      this.element.addEventListener("mouseenter", this.onMouseenter);
      this.element.addEventListener("mouseleave", this.onMouseleave);
      this.element.addEventListener("focusin", this.onFocusin);
      this.element.addEventListener("focusout", this.onFocusout);
      reducedMotionMedia && reducedMotionMedia.addEventListener("change", this.onReducedMotionMediaChange);
    }
  }

  @Watch("disabled")
  @Watch("preventMouseDrag")
  preventMouseDragWatcher() {
    if (this.preventMouseDrag || this.disabled) {
      this.disableTimeoutId && window.clearTimeout(this.disableTimeoutId);
      this.disableTimeoutId = null;
      this.scrollElement && this.scrollElement.removeEventListener("mousedown", this.startDragging);
      this.lastX = null;
    } else {
      this.scrollElement && this.scrollElement.addEventListener("mousedown", this.startDragging);
    }
  }

  @Watch("autoplay")
  @Watch("autoplayDisabledByMotionMedia")
  @Watch("autoplayDisabledByMouse")
  @Watch("autoplayDisabledByFocus")
  @Watch("autoplayDisabledByScroll")
  @Watch("autoplayDisabledByDrag")
  @Watch("autoplayTimeout")
  @Watch("disabled")
  autoplayRunningWatcher() {
    if (
      !this.autoplay
      || this.disabled
      || this.autoplayDisabledByMotionMedia
      || this.autoplayDisabledByMouse
      || this.autoplayDisabledByFocus
      || this.autoplayDisabledByScroll
      || this.autoplayDisabledByDrag
    ) {
      this.autoplayIntervalId && window.clearInterval(this.autoplayIntervalId);
      this.autoplayIntervalId = null;
      this.autoplayRunning = false;
    } else {
      this.autoplayIntervalId && window.clearInterval(this.autoplayIntervalId);
      this.autoplayIntervalId = window.setInterval(this.autoplayInterval, this.autoplayTimeout);
      this.autoplayRunning = true;
    }
  }

  @Watch("slideElements")
  @Watch("currentSwipeSlides")
  snapAlignWatcher() {
    this.slideElements.forEach((slide, index) => {
      const shouldSnap = (index - this.currentSwipeSlides) % this.currentSwipeSlides === 0;

      if (shouldSnap) {
        slide.style.removeProperty("scroll-snap-align");
      } else {
        slide.style.setProperty("scroll-snap-align", "none");
      }
    });
  }

  @Watch("slide")
  @Watch("variant")
  @Watch("slideElements")
  @Watch("currentSlides")
  @Watch("tabThrough")
  inertWatcher() {
    this.slideElements.forEach((slide, index) => {
      const isActive = this.tabThrough
        || this.variant === "cards"
        || index >= this.slide && index < this.slide + this.currentSlides;

      slide.inert = !isActive;

      if (isActive) {
        slide.removeAttribute("aria-hidden");
      } else {
        slide.setAttribute("aria-hidden", isActive ? "false" : "true");
      }
    });
  }

  @Watch("variant")
  @Watch("tabThrough")
  @Watch("nextElements")
  @Watch("previousElements")
  prevNextNavigationWatcher() {
    const tabThrough = this.tabThrough || this.variant === "cards";

    const setAriaAttributes = (element) => {
      const isStzh = isStzhElement(element);

      if (tabThrough) {
        element.setAttribute("aria-hidden", "true");
        element.setAttribute(isStzh ? "a11y-tabindex" : "tabindex", "-1");
      } else {
        element.removeAttribute("aria-hidden");
        element.removeAttribute(isStzh ? "a11y-tabindex" : "tabindex");
      }
    }

    this.nextElements.forEach(setAriaAttributes);
    this.previousElements.forEach(setAriaAttributes);
  }

  @Watch("currentSlides")
  @Watch("slideElements")
  @Watch("nextElements")
  @Watch("previousElements")
  @Watch("navigationTextElements")
  prevNextHiddenWatcher() {
    const hideElements = this.slideElements.length <= this.currentSlides;

    const setHiddenAttribute = (element) => {
      if (hideElements) {
        element.setAttribute("hidden", "");
      } else {
        element.removeAttribute("hidden");
      }
    };

    this.navigationTextElements.forEach(setHiddenAttribute);
    this.nextElements.forEach(setHiddenAttribute);
    this.previousElements.forEach(setHiddenAttribute);
  }

  @Watch("disabled")
  @Watch("slide")
  @Watch("currentSlides")
  @Watch("slideElements")
  @Watch("nextElements")
  @Watch("previousElements")
  nextPrevDisabledWatcher() {
    if (!this.scrollElement) {
      return;
    }

    const reachedFirstSlide = this.slide === 0;
    const reachedLastSlide = this.hasReachedLastSlide();

    this.nextElements.forEach((element) => {
      const isStzh = isStzhElement(element);
      element.setAttribute(isStzh ? "a11y-disabled" : "aria-disabled", reachedLastSlide || this.disabled ? "true" : "false");
    });

    this.previousElements.forEach((element) => {
      const isStzh = isStzhElement(element);
      element.setAttribute(isStzh ? "a11y-disabled" : "aria-disabled", reachedFirstSlide || this.disabled ? "true" : "false");
    });
  }

  @Watch("nextElements")
  nextElementsWatcher() {
    this.nextElements.forEach((nextElement) => {
      const isStzh = isStzhElement(nextElement);

      nextElement.addEventListener("click", this.onNextClick);
      nextElement.setAttribute(isStzh ? "a11y-controls" : "aria-controls", `${this.carouselId}-scroll`);
    });
  }

  @Watch("previousElements")
  previousElementsWatcher() {
    this.previousElements.forEach((previousElement) => {
      const isStzh = isStzhElement(previousElement);

      previousElement.addEventListener("click", this.onPreviousClick);
      previousElement.setAttribute(isStzh ? "a11y-controls" : "aria-controls", `${this.carouselId}-scroll`);
    });
  }

  /** Whether autoplay is currently running */
  @State() autoplayRunning: boolean = false;

  /** Whether autoplay is currently disabled by reduced motion media */
  @State() autoplayDisabledByMotionMedia: boolean = false;

  /** Whether autoplay is currently disabled by mouse */
  @State() autoplayDisabledByMouse: boolean = false;

  /** Whether autoplay is currently disabled by focus */
  @State() autoplayDisabledByFocus: boolean = false;

  /** Whether autoplay is currently disabled by scroll */
  @State() autoplayDisabledByScroll: boolean = false;

  /** Whether autoplay is currently disabled by drag */
  @State() autoplayDisabledByDrag: boolean = false;

  /** Whether user is currently dragging with mouse */
  @State() dragging: boolean = false;

  /** Whether user is currently scrolling */
  @State() scrolling: boolean = false;

  /** Aria live text */
  @State() ariaLive: string = "";

  /** Currently shown slides */
  @State() currentSlides: number = 1;

  /** How many slides should be moved currently */
  @State() currentSwipeSlides: number = 1;

  /** Slide elements */
  @State() slideElements: HTMLElement[] = [];

  /** Previous elements */
  @State() previousElements: HTMLElement[] = [];

  /** Next elements */
  @State() nextElements: HTMLElement[] = [];

  /** Navigation text elements */
  @State() navigationTextElements: HTMLElement[] = [];

  private scrollElement: HTMLElement;

  /** Unique generated id */
  private carouselId: string;

  /** Current scroll position */
  private slideScrollLeft: number = 0;

  /** Flag whether scrolling was done by user or not */
  private scrolledByAutoplay: boolean = false;

  /** Timeout ID used to catch the end of scroll events */
  private scrollTimeoutId: number = null;

  /** Interval ID for autoplay */
  private autoplayIntervalId: number;

  /** Last drag event position */
  private lastX: number = null;

  /** Where the dragging started */
  private startX: number = null;

  /** Where the dragging started scroll position */
  private startScrollLeft: number = null;

  /** Timeout ID for a smooth drag release */
  private disableTimeoutId: number = null;

  /** Calculated size of a single item */
  private itemSize: number;

  /** Resize observer used to update item size */
  private resizeObserver: ResizeObserver;

  /** Mutation observer used to keep track of slides */
  private mutationObserver: MutationObserver;

  /** Whether user has just stopped dragging */
  private stoppedDragging: boolean = false;

  private scrollToSlide(index, behaviour: "auto" | "smooth" = "smooth") {
    if (this.disabled) {
      return;
    }

    this.scrollElement.scrollTo({
      left: index * this.itemSize,
      behavior: reducedMotionMedia.matches ? "auto" : behaviour
    });
  }

  /** Width of a slide  */
  private sizingMethod() {
    const css = window.getComputedStyle(this.scrollElement);

    return ((this.scrollElement.firstElementChild as HTMLElement) || this.scrollElement).offsetWidth
      + parseInt(css.getPropertyValue("column-gap"), 10);
  }

  /**
   * Calculates the active slide.
   * The scroll-snap-type property makes sure that the container snaps perfectly to integer multiples.
   */
  private calculateSlide() {
    // Math.round: Dispatch 'stzhSlidePassed' events around the center of each slide
    // Math.ceil: Dispatch 'stzhSlidePassed' events as soon as the next one is visible
    // Math.floor: Dispatch 'stzhSlidePassed' events only when the next one is fully visible
    return Math.round(this.scrollElement.scrollLeft / this.itemSize);
  }

  private scrollToPreviousSnapPoint(behaviour: "auto" | "smooth" = "smooth") {
    if (this.disabled) {
      return;
    }

    let previousIndex = this.slide;
    let canSnap = false;

    while (!canSnap && previousIndex > 0) {
      previousIndex = previousIndex - 1;
      canSnap = (previousIndex - this.currentSwipeSlides) % this.currentSwipeSlides === 0;
    }

    this.slideTo(previousIndex, behaviour);
  }

  private scrollToNextSnapPoint(behaviour: "auto" | "smooth" = "smooth") {
    if (this.disabled) {
      return;
    }

    this.slideTo(this.slide + this.currentSwipeSlides, behaviour);
  }

  private update() {
    this.slide = this.calculateSlide();
    this.slideScrollLeft = this.slide * this.itemSize;

    this.onMutation();
  }

  private hasReachedLastSlide() {
    const { scrollLeft, offsetWidth, scrollWidth } = this.scrollElement;
    return Math.ceil(scrollLeft + offsetWidth) >= scrollWidth;
  }

  private onReducedMotionMediaChange = () => {
    this.autoplayDisabledByMotionMedia = reducedMotionMedia.matches;
  }

  private onMouseenter = () => {
    if (!this.preventAutoplayPause) {
      this.autoplayDisabledByMouse = true;
    }
  }

  private onMouseleave = () => {
    this.autoplayDisabledByMouse = false;
  }

  private onFocusin = () => {
    if (!this.preventAutoplayPause) {
      this.autoplayDisabledByFocus = true;
    }
  }

  private onFocusout = () => {
    this.autoplayDisabledByFocus = false;
  }

  private onPreviousClick = () => {
    this.scrollToPreviousSnapPoint();
  }

  private onNextClick = () => {
    this.scrollToNextSnapPoint();
  }

  private onSlideResize = () => {
    if (this.scrollElement) {
      this.itemSize = this.sizingMethod();
    }
  }

  private onMutation = () => {
    if (!this.scrollElement) {
      return;
    }

    this.slideElements = Array.from(this.scrollElement.children) as HTMLElement[];

    this.slideElements.forEach((slide, index) => {
      slide.setAttribute("role", "group");
      slide.setAttribute("aria-roledescription", "slide");
      slide.setAttribute("aria-label", this.localization.slideLabel
        .replace(/\{current\}/gi, (index + 1).toString())
        .replace(/\{total\}/gi, this.slideElements.length.toString())
      );
    });

    this.previousElements = Array.from(this.element.querySelectorAll("[data-stzh-carousel-previous]"));
    this.nextElements = Array.from(this.element.querySelectorAll("[data-stzh-carousel-next]"));
    this.navigationTextElements = Array.from(this.element.querySelectorAll("[data-stzh-carousel-navigation-text]"));
  }

  private onScroll = () => {
    if (this.scrollTimeoutId === null) {
      const direction = (this.scrollElement.scrollLeft > this.slideScrollLeft) ? 1 : -1;

      this.scrolling = true;

      if (!this.scrolledByAutoplay) {
        if (this.preventAutoplayStop) {
          this.autoplayDisabledByScroll = true;
        } else {
          this.autoplay = false;
        }
      }

      this.stzhSlideStart.emit({
        component: "stzh-carousel",
        index: this.slide + direction
      });
    }

    if (this.slide !== this.calculateSlide()) {
      this.update();
      this.stzhSlidePassed.emit({
        component: "stzh-carousel",
        index: this.slide
      });
    }

    this.scrollTimeoutId && window.clearTimeout(this.scrollTimeoutId);
    this.scrollTimeoutId = window.setTimeout(this.onScrollEnd, this.scrollTimeout);
  }

  private onScrollEnd = () => {
    this.scrollTimeoutId = null;
    this.update();

    this.scrolling = false;
    this.scrolledByAutoplay = false;
    this.autoplayDisabledByScroll = false;

    if (this.stoppedDragging) {
      this.stoppedDragging = false;
      this.scrollElement.style.removeProperty("scroll-behavior");
      this.scrollElement.style.removeProperty("scroll-snap-type");
    }

    if (this.tabThrough || this.variant === "cards") {
      this.ariaLive = "";
    } else {
      this.ariaLive = (this.currentSlides > 1 ? this.localization.slidesLabel : this.localization.slideLabel)
        .replace(/\{(current|from)\}/gi, (this.slide + 1).toString())
        .replace(/\{to\}/gi, (this.slide + this.currentSlides).toString())
        .replace(/\{total\}/gi, this.slideElements.length.toString());
    }

    this.stzhSlideEnd.emit({
      component: "stzh-carousel",
      index: this.slide
    });
  }

  private onMediaChange = () => {
    const ultra = media("ultra").matches;
    const large = media("large").matches;
    const medium = media("medium").matches;
    const small = media("small").matches;

    if (ultra && this.slidesUltra !== 0) {
      this.currentSlides = this.slidesUltra;
    } else if (large && this.slidesLarge !== 0) {
      this.currentSlides = this.slidesLarge;
    } else if (medium && this.slidesMedium !== 0) {
      this.currentSlides = this.slidesMedium;
    } else if (small && this.slidesSmall !== 0) {
      this.currentSlides = this.slidesSmall;
    } else {
      this.currentSlides = this.slides;
    }

    // if (ultra && this.swipeSlidesUltra !== 0) {
    //   this.currentSwipeSlides = this.swipeSlidesUltra;
    // } else if (large && this.swipeSlidesLarge !== 0) {
    //   this.currentSwipeSlides = this.swipeSlidesLarge;
    // } else if (medium && this.swipeSlidesMedium !== 0) {
    //   this.currentSwipeSlides = this.swipeSlidesMedium;
    // } else if (small && this.swipeSlidesSmall !== 0) {
    //   this.currentSwipeSlides = this.swipeSlidesSmall;
    // } else {
    //   this.currentSwipeSlides = this.swipeSlides;
    // }

    this.currentSwipeSlides = this.currentSlides;
  }

  /** Scroll the slider the appropriate amount of pixels and update the last event position */
  private mouseMove = (event: MouseEvent) => {
    const distance = this.lastX - event.clientX;
    this.lastX = event.clientX;

    this.scrollElement.scrollLeft += distance;
  }

  /** Clear disable timeout, set up variables and styles and attach the listener. */
  private startDragging = (event: MouseEvent) => {
    event.preventDefault();

    this.disableTimeoutId && window.clearTimeout(this.disableTimeoutId);
    this.disableTimeoutId = null;

    this.startX = this.lastX = event.clientX;
    this.startScrollLeft = this.scrollElement.scrollLeft;
    this.scrollElement.style.setProperty("scroll-behavior", "auto");
    this.scrollElement.style.setProperty("scroll-snap-type", "unset");

    this.dragging = true;

    if (this.preventAutoplayStop) {
      this.autoplayDisabledByDrag = true;
    } else {
      this.autoplay = false;
    }

    window.addEventListener("mousemove", this.mouseMove);
    window.addEventListener("mouseup", this.stopDragging, { capture: true });
    window.addEventListener("click", this.checkClick, { capture: true });
  }

  /** User stopped dragging */
  private stopDragging = (event: MouseEvent) => {
    event.preventDefault();

    const distance = Math.abs(this.startX - this.lastX);
    const minimumReached = distance > this.distanceQuickSwipe;
    const direction = (this.lastX > this.startX) ? 1 : -1;

    this.dragging = false;
    this.autoplayDisabledByDrag = false;

    const endDraddingLeft = this.scrollElement.scrollLeft;

    this.scrollElement.style.removeProperty("scroll-snap-type");
    const finalLeft = this.scrollElement.scrollLeft;

    this.scrollElement.style.setProperty("scroll-snap-type", "unset");
    this.scrollElement.scrollTo({ left: endDraddingLeft, behavior: "auto" });

    if (finalLeft !== this.startScrollLeft || !minimumReached) {
      this.scrollElement.scrollTo({ left: finalLeft, behavior: reducedMotionMedia.matches ? "auto" : "smooth" });
    } else if (direction === 1) {
      this.slideToPrevious();
    } else {
      this.slideToNext();
    }

    window.removeEventListener("mousemove", this.mouseMove);
    window.removeEventListener("mouseup", this.stopDragging, { capture: true });

    this.stoppedDragging = true;
  }

  private checkClick = (event: MouseEvent) => {
    const distance = Math.abs(this.startX - this.lastX);
    const minimumReached = distance > this.distancePreventClick;

    if (minimumReached) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.lastX = null;

    window.removeEventListener("click", this.checkClick, { capture: true });
  }

  private autoplayInterval = () => {
    this.scrolledByAutoplay = true;

    if (this.hasReachedLastSlide()) {
      this.slideTo(0);
    } else {
      this.slideToNext();
    }
  }

  async componentWillLoad() {
    this.carouselId = `stzh-carousel-${carouselCounter++}`;
    this.onMediaChange();

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "carousel");
    }
  }

  componentDidLoad() {
    this.itemSize = this.sizingMethod();
    this.update();

    this.disabledWatcher();
    this.preventMouseDragWatcher();
    this.autoplayRunningWatcher();
  }

  connectedCallback() {
    addMediaChangeListener(this.onMediaChange);

    this.resizeObserver = new ResizeObserver(this.onSlideResize)
    this.resizeObserver.observe(this.element)

    this.mutationObserver = new MutationObserver(this.onMutation);
    this.mutationObserver.observe(this.element, {
      childList: true,
      subtree: true
    });
  }

  disconnectedCallback() {
    this.scrollTimeoutId && window.clearTimeout(this.scrollTimeoutId);
    this.scrollTimeoutId = null;

    removeMediaChangeListener(this.onMediaChange);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  render() {
    const classes = {
      "stzh-carousel": true,
      "stzh-carousel--is-disabled": this.disabled,
      "stzh-carousel--is-draggable": !this.preventMouseDrag,
      "stzh-carousel--is-non-interactive": this.nonInteractive,
      "stzh-carousel--is-dragging": this.dragging,
      "stzh-carousel--is-scrolling": this.scrolling,
      "stzh-carousel--has-autoplay": this.autoplay,
      "stzh-carousel--is-autoplay-running": this.autoplayRunning,
    };

    return (
      <Host
        aria-roledescription="carousel"
        style={{
          '--slides': this.currentSlides.toString()
        }}
      >
        <div class={classes}>
          <slot name="actions"></slot>
          <div
            class="stzh-carousel__vhidden"
            aria-live={this.autoplayDisabledByFocus || this.autoplayDisabledByMouse ? "polite" : "off"}
          >
            {this.ariaLive}
          </div>
          <div
            ref={(el) => (this.scrollElement = el as HTMLDivElement)}
            id={`${this.carouselId}-scroll`}
            class="stzh-carousel__scroll"
          >
            <slot></slot>
          </div>
          {this.withTrack &&
            <div class="stzh-carousel__indicator">
              <div
                class="stzh-carousel__indicator-track"
                style={{
                  left: `${100 / (this.slideElements?.length || 1) * this.slide}%`,
                  width: `${100 / (this.slideElements?.length || 1) * this.currentSlides || 0}%`
                }}
              ></div>
            </div>
          }
        </div>
      </Host>
    );
  }
}
